import { useEffect, useState } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { HeartPulse, CircleDollarSign, Calendar, Cake, ChevronRight, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";

/**
 * Director-friendly monthly summary widget shown on the Dashboard.
 * Pulls aggregated counters via /api/dashboard/director-summary and renders
 * 4 quick-glance cards (medical, fees, upcoming events, birthdays).
 */
export default function DirectorSummary({ teamId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teamId) return;
    setLoading(true);
    api
      .get(`/dashboard/director-summary?team_id=${teamId}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [teamId]);

  if (loading) {
    return <Card className="p-6 text-sm text-slate-500" data-testid="director-summary-loading">Caricamento riepilogo...</Card>;
  }
  if (!data) return null;

  return (
    <div data-testid="director-summary">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="label-eyebrow">Riepilogo dirigente</div>
          <h2 className="font-heading text-2xl font-semibold">Promemoria della settimana</h2>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Medical */}
        <Card className="p-5 border-slate-200" data-testid="ds-medical">
          <Header icon={HeartPulse} title="Visite mediche" color="rose" />
          <div className="mt-3 space-y-1.5">
            {data.medical.expired > 0 && (
              <Row danger label="Scadute" value={data.medical.expired} />
            )}
            <Row label="In scadenza (30gg)" value={data.medical.expiring_soon} muted={data.medical.expiring_soon === 0} />
          </div>
          {data.medical.samples?.length > 0 && (
            <ul className="mt-4 space-y-1">
              {data.medical.samples.slice(0, 4).map((p) => (
                <li key={p.id} className="flex items-center justify-between text-xs">
                  <span className="truncate text-slate-700">{p.last_name} {p.first_name}</span>
                  <span className={`text-[10px] font-mono ml-2 ${p.medical_expiry < new Date().toISOString().slice(0, 10) ? "text-red-600" : "text-slate-500"}`}>
                    {formatDate(p.medical_expiry)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/app/anagrafica" className="mt-3 inline-flex items-center text-xs text-rose-600 hover:underline">
            Vai all&apos;anagrafica <ChevronRight size={12} />
          </Link>
        </Card>

        {/* Fees */}
        <Card className="p-5 border-slate-200" data-testid="ds-fees">
          <Header icon={CircleDollarSign} title="Quote non pagate" color="amber" />
          <div className="mt-3 space-y-1.5">
            <Row label="Quote aperte" value={data.fees.unpaid_count} muted={data.fees.unpaid_count === 0} />
            <Row label="Totale" value={`€ ${Number(data.fees.unpaid_total || 0).toFixed(2)}`} muted={!data.fees.unpaid_total} />
          </div>
          {data.fees.samples?.length > 0 && (
            <ul className="mt-4 space-y-1">
              {data.fees.samples.slice(0, 4).map((p) => (
                <li key={p.id} className="flex items-center justify-between text-xs">
                  <span className="truncate text-slate-700">{p.description || "Quota"}</span>
                  <span className="text-[10px] font-mono ml-2 text-amber-700">€{Number(p.amount).toFixed(2)}</span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/app/amministrazione" className="mt-3 inline-flex items-center text-xs text-amber-700 hover:underline">
            Vai a amministrazione <ChevronRight size={12} />
          </Link>
        </Card>

        {/* Events */}
        <Card className="p-5 border-slate-200" data-testid="ds-events">
          <Header icon={Calendar} title="Prossimi 7 giorni" color="sky" />
          <div className="mt-3 space-y-1.5">
            <Row label="Allenamenti" value={data.events.trainings.length} muted={!data.events.trainings.length} />
            <Row label="Partite" value={data.events.matches.length} muted={!data.events.matches.length} />
          </div>
          {(data.events.matches.length + data.events.trainings.length) > 0 && (
            <ul className="mt-4 space-y-1">
              {data.events.matches.slice(0, 2).map((m) => (
                <li key={m.id} className="flex items-center justify-between text-xs">
                  <span className="truncate text-slate-700">⚽ {m.opponent || m.title}</span>
                  <span className="text-[10px] font-mono ml-2 text-sky-700">{formatDate(m.match_date)}</span>
                </li>
              ))}
              {data.events.trainings.slice(0, 2).map((t) => (
                <li key={t.id} className="flex items-center justify-between text-xs">
                  <span className="truncate text-slate-700">🏃 {t.title || "Allenamento"}</span>
                  <span className="text-[10px] font-mono ml-2 text-sky-700">{formatDate(t.date)}</span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/app/calendario" className="mt-3 inline-flex items-center text-xs text-sky-600 hover:underline">
            Vai al calendario <ChevronRight size={12} />
          </Link>
        </Card>

        {/* Birthdays */}
        <Card className="p-5 border-slate-200" data-testid="ds-birthdays">
          <Header icon={Cake} title="Compleanni 7gg" color="purple" />
          <div className="mt-3">
            <Row label="In arrivo" value={data.birthdays?.length || 0} muted={!data.birthdays?.length} />
          </div>
          {data.birthdays?.length > 0 ? (
            <ul className="mt-4 space-y-1">
              {data.birthdays.slice(0, 5).map((b) => (
                <li key={b.id} className="flex items-center justify-between text-xs">
                  <span className="truncate text-slate-700">🎂 {b.first_name} {b.last_name}</span>
                  <span className="text-[10px] font-mono ml-2 text-purple-700">{formatDate(b.next_birthday)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-xs text-slate-400">Nessun compleanno in vista.</p>
          )}
        </Card>
      </div>
    </div>
  );
}

function Header({ icon: Icon, title, color }) {
  const cls = {
    rose: "bg-rose-100 text-rose-600",
    amber: "bg-amber-100 text-amber-700",
    sky: "bg-sky-100 text-sky-600",
    purple: "bg-purple-100 text-purple-700",
  }[color];
  return (
    <div className="flex items-center gap-2.5">
      <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${cls}`}>
        <Icon size={17} />
      </div>
      <div className="font-medium text-sm">{title}</div>
    </div>
  );
}

function Row({ label, value, muted, danger }) {
  return (
    <div className={`flex items-center justify-between text-xs ${muted ? "opacity-50" : ""}`}>
      <span className="text-slate-500 inline-flex items-center gap-1">
        {danger && <AlertTriangle size={11} className="text-red-500" />}
        {label}
      </span>
      <span className={`font-heading font-bold ${danger ? "text-red-600" : "text-slate-900"}`}>{value}</span>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
}
